import * as pc from "playcanvas";

/**
 * CS:GO-style molotov.
 * 
 * Original look: a translucent bright-green glass bottle with amber
 * gasoline visible inside (~70% fill), silver duct tape banding a cream
 * rag plug to the neck, a fuel-soaked cloth tail draping over the
 * shoulder, and a layered additive flame. Every decal hugs the glass so
 * the bottle reads from any angle while tumbling in flight.
 */

export type AddPart = (
  parent: pc.Entity,
  type: "box" | "cylinder" | "capsule" | "sphere",
  name: string,
  scale: [number, number, number],
  pos: [number, number, number],
  euler: [number, number, number],
  material: pc.StandardMaterial,
) => unknown;

export type Mat = (r: number, g: number, b: number, gloss?: number) => pc.StandardMaterial;

/** Translucent green bottle glass — high gloss so scene lights glint off it. */
function glassMat(r: number, g: number, b: number, opacity: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(r, g, b);
  m.gloss = 0.97;
  m.opacity = opacity;
  m.blendType = pc.BLEND_NORMAL;
  m.update();
  return m;
}

/** Opaque gasoline with a warm emissive base so it glows through the glass. */
function fuelMat(r: number, g: number, b: number, er: number, eg: number, eb: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(r, g, b);
  m.emissive = new pc.Color(er, eg, eb);
  m.gloss = 0.88;
  m.update();
  return m;
}

/** Additive self-illuminated flame layer (same blend the fire patches use). */
function flameMat(r: number, g: number, b: number, intensity: number): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(0, 0, 0);
  m.emissive = new pc.Color(r, g, b);
  m.emissiveIntensity = intensity;
  m.useLighting = false;
  m.blendType = pc.BLEND_ADDITIVE;
  m.depthWrite = false;
  m.update();
  return m;
}

/** Bright glossy streak — fake specular highlight decal on the glass. */
function shineMat(): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(0.85, 0.95, 0.88);
  m.emissive = new pc.Color(0.18, 0.22, 0.18);
  m.gloss = 0.98;
  m.opacity = 0.5;
  m.blendType = pc.BLEND_NORMAL;
  m.update();
  return m;
}

/** Glowing ember band where the charred wick meets the flame. */
function emberMat(): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(0.1, 0.04, 0.01);
  m.emissive = new pc.Color(1.0, 0.32, 0.05);
  m.emissiveIntensity = 1.8;
  m.useLighting = false;
  m.gloss = 0.3;
  m.update();
  return m;
}

/** Cloth lit from above by the flame (warm tint on the plug top). */
function litClothMat(): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(0.88, 0.84, 0.72);
  m.emissive = new pc.Color(0.50, 0.20, 0.04);
  m.gloss = 0.2;
  m.update();
  return m;
}

/** Faint dark translucent puff capping the flame. */
function smokeMat(): pc.StandardMaterial {
  const m = new pc.StandardMaterial();
  m.diffuse = new pc.Color(0, 0, 0);
  m.emissive = new pc.Color(0.055, 0.055, 0.06);
  m.useLighting = false;
  m.opacity = 0.22;
  m.blendType = pc.BLEND_NORMAL;
  m.depthWrite = false;
  m.update();
  return m;
}

/**
 * Build the molotov into `parent`. Geometry is centred on the local
 * origin so it drops cleanly into the in-hand rig and the in-flight
 * marble alike. Total height is roughly base y -0.165 to smoke cap y +0.31.
 */
export function buildMolotov(parent: pc.Entity, addPart: AddPart, mat: Mat): void {
  const glassClear = glassMat(0.36, 0.54, 0.30, 0.46);
  const glassDeep = glassMat(0.15, 0.26, 0.12, 0.74);
  const glassShine = shineMat();
  const fuel = fuelMat(0.74, 0.43, 0.10, 0.20, 0.08, 0.015);
  const fuelDeep = fuelMat(0.48, 0.25, 0.05, 0.10, 0.04, 0.008);
  const fuelTop = fuelMat(0.95, 0.60, 0.16, 0.34, 0.15, 0.03);
  const tape = mat(0.52, 0.53, 0.55, 0.62);
  const tapeDark = mat(0.36, 0.37, 0.39, 0.5);
  const cloth = mat(0.88, 0.84, 0.72, 0.16);
  const clothFold = mat(0.68, 0.63, 0.50, 0.14);
  const clothWet = mat(0.44, 0.31, 0.16, 0.42);
  const clothLit = litClothMat();
  const charred = mat(0.05, 0.042, 0.034, 0.1);
  const ember = emberMat();
  const flameRed = flameMat(1.0, 0.22, 0.02, 1.5);
  const flameOrange = flameMat(1.0, 0.52, 0.06, 2.0);
  const flameHot = flameMat(1.0, 0.80, 0.25, 2.5);
  const flameCore = flameMat(1.0, 0.95, 0.62, 3.0);
  const smoke = smokeMat();

  // Bottle: translucent green glass — chunky heel, domed shoulder, slim neck.
  addPart(parent, "cylinder", "molotov-glass-punt", [0.050, 0.012, 0.050], [0, -0.159, 0], [0, 0, 0], glassDeep);
  addPart(parent, "cylinder", "molotov-base-heel", [0.064, 0.020, 0.064], [0, -0.146, 0], [0, 0, 0], glassDeep);
  addPart(parent, "cylinder", "molotov-mold-ring", [0.0635, 0.005, 0.0635], [0, -0.133, 0], [0, 0, 0], glassDeep);
  addPart(parent, "cylinder", "molotov-body-glass", [0.062, 0.186, 0.062], [0, -0.048, 0], [0, 0, 0], glassClear);
  addPart(parent, "sphere", "molotov-shoulder-glass", [0.062, 0.062, 0.062], [0, 0.046, 0], [0, 0, 0], glassClear);
  addPart(parent, "cylinder", "molotov-neck-taper", [0.035, 0.026, 0.035], [0, 0.082, 0], [0, 0, 0], glassClear);
  addPart(parent, "cylinder", "molotov-neck", [0.024, 0.080, 0.024], [0, 0.126, 0], [0, 0, 0], glassClear);
  addPart(parent, "cylinder", "molotov-mouth-lip", [0.030, 0.013, 0.030], [0, 0.170, 0], [0, 0, 0], glassDeep);

  // Gasoline visible through the glass: dark settled bottom, amber body,
  // bright meniscus line at ~70% fill.
  addPart(parent, "cylinder", "molotov-fuel-deep", [0.0555, 0.018, 0.0555], [0, -0.130, 0], [0, 0, 0], fuelDeep);
  addPart(parent, "cylinder", "molotov-fuel-body", [0.055, 0.118, 0.055], [0, -0.062, 0], [0, 0, 0], fuel);
  addPart(parent, "cylinder", "molotov-fuel-line", [0.0565, 0.007, 0.0565], [0, -0.001, 0], [0, 0, 0], fuelTop);

  // Fake specular streaks hugging the glass (rotY keeps each tangent to the
  // surface so they still read as glints when the bottle spins mid-air).
  // Detail features live on +z — the face the first-person camera sees.
  addPart(parent, "box", "molotov-shine-main", [0.005, 0.150, 0.003], [-0.0186, -0.045, 0.0266], [0, 35, -3], glassShine);
  addPart(parent, "box", "molotov-shine-right", [0.0035, 0.085, 0.003], [0.0217, -0.070, 0.0242], [0, -42, 2], glassShine);
  addPart(parent, "box", "molotov-shine-neck", [0.003, 0.014, 0.0025], [-0.0057, 0.129, 0.0123], [0, 25, -2], glassShine);
  addPart(parent, "box", "molotov-shine-shoulder", [0.004, 0.020, 0.0025], [-0.0150, 0.060, 0.0235], [0, 33, -28], glassShine);

  // Silver duct tape banding the rag to the neck, with a peeled end tab.
  addPart(parent, "cylinder", "molotov-tape-low", [0.0305, 0.020, 0.0305], [0, 0.097, 0], [0, 0, 0], tape);
  addPart(parent, "cylinder", "molotov-tape-high", [0.0315, 0.013, 0.0315], [0, 0.113, 0], [0, 0, 0], tape);
  addPart(parent, "cylinder", "molotov-tape-seam", [0.0322, 0.0035, 0.0322], [0, 0.1065, 0], [0, 0, 0], tapeDark);

  // Rag: cream cloth wrapped under the lip, knot at the front, stuffed plug,
  // and a fuel-soaked tail draping down onto the shoulder.
  addPart(parent, "cylinder", "molotov-rag-wrap", [0.035, 0.026, 0.035], [0, 0.151, 0], [0, 0, 0], cloth);
  addPart(parent, "cylinder", "molotov-rag-wrap-fold", [0.036, 0.009, 0.036], [0, 0.142, 0], [0, 0, 0], clothFold);
  addPart(parent, "sphere", "molotov-rag-knot", [0.024, 0.018, 0.020], [-0.003, 0.154, 0.0195], [0, 10, 0], cloth);
  addPart(parent, "sphere", "molotov-rag-plug", [0.026, 0.044, 0.024], [-0.002, 0.186, 0], [0, 0, -7], cloth);
  addPart(parent, "sphere", "molotov-rag-plug-top", [0.020, 0.014, 0.018], [0.002, 0.208, -0.001], [0, 0, 0], clothLit);
  addPart(parent, "sphere", "molotov-rag-tail", [0.014, 0.092, 0.011], [-0.013, 0.105, 0.0215], [-5, 18, -10], clothWet);
  addPart(parent, "sphere", "molotov-rag-tail-end", [0.013, 0.052, 0.010], [-0.0205, 0.040, 0.0245], [-4, 40, -12], clothFold);

  // Charred wick with a glowing ember band feeding the flame.
  addPart(parent, "sphere", "molotov-wick-char", [0.015, 0.032, 0.014], [0.004, 0.214, -0.001], [0, 0, -6], charred);
  addPart(parent, "sphere", "molotov-wick-ember", [0.017, 0.010, 0.016], [0.004, 0.220, -0.001], [0, 0, 0], ember);

  // Flame: additive layers — red shell, orange body, hot column, white-hot
  // core at the base, licking side tongue, detached wisp, faint smoke cap.
  addPart(parent, "sphere", "molotov-flame-base", [0.026, 0.022, 0.024], [0.004, 0.226, -0.001], [0, 0, 0], flameOrange);
  addPart(parent, "sphere", "molotov-flame-outer", [0.042, 0.082, 0.036], [0.006, 0.258, -0.002], [0, 0, 0], flameRed);
  addPart(parent, "sphere", "molotov-flame-mid", [0.030, 0.060, 0.026], [0.005, 0.252, -0.001], [0, 0, 0], flameOrange);
  addPart(parent, "sphere", "molotov-flame-hot", [0.019, 0.042, 0.017], [0.005, 0.244, -0.001], [0, 0, 0], flameHot);
  addPart(parent, "sphere", "molotov-flame-core", [0.010, 0.022, 0.010], [0.005, 0.238, -0.001], [0, 0, 0], flameCore);
  addPart(parent, "sphere", "molotov-flame-tongue", [0.013, 0.034, 0.011], [-0.011, 0.268, 0.003], [0, 0, 16], flameOrange);
  addPart(parent, "sphere", "molotov-flame-wisp", [0.009, 0.024, 0.008], [0.014, 0.294, -0.004], [0, 0, -14], flameRed);
  addPart(parent, "sphere", "molotov-smoke-cap", [0.016, 0.012, 0.015], [0.008, 0.306, -0.004], [0, 0, 0], smoke);
}
